import values from '../values/values';
import { getWorkSeriesMeta } from './graphMultiWorkUtils';

/** Escala padrão dos mapas: valores menores (vermelho) → maiores (azul), como no mapa de altitude. */
export const MAP_LEGEND_COLOR_LOW = '#ff0000';
export const MAP_LEGEND_COLOR_HIGH = '#1976d2';
export const MAP_LEGEND_MID_COLORS = [
    '#FF5733',
    '#FFCA28',
    '#F4e538',
    '#36e236',
    '#118e11',
];

/** Cores usadas pela legenda de valor dos mapas (normalizadas). Evitar na legenda multi-trabalho. */
export const getMapLegendReservedColors = () => {
    const colors = [
        MAP_LEGEND_COLOR_LOW,
        ...MAP_LEGEND_MID_COLORS,
        MAP_LEGEND_COLOR_HIGH,
    ];
    return new Set(colors.map((c) => String(c).toLowerCase()));
};

/**
 * Monta legendas de valor no padrão do mapa de relevo:
 * abaixo do mínimo = vermelho; faixas intermediárias; acima do máximo = azul.
 */
export const createMapValueLegends = (newMin, newMax, divisions = 5) => {
    const interval = (newMax - newMin) / divisions;
    const newLegends = [];

    newLegends.push({
        id: 0,
        min: null,
        max: newMin,
        color: MAP_LEGEND_COLOR_LOW,
        show: true,
    });

    for (let i = 0; i < divisions; i++) {
        const minLimit = newMin + interval * i;
        const maxLimit = newMin + interval * (i + 1);
        newLegends.push({
            id: i + 1,
            min: parseFloat(minLimit.toFixed(1)),
            max: parseFloat(maxLimit.toFixed(1)),
            color: MAP_LEGEND_MID_COLORS[i] || MAP_LEGEND_MID_COLORS[MAP_LEGEND_MID_COLORS.length - 1],
            show: true,
        });
    }

    newLegends.push({
        id: divisions + 1,
        min: newMax,
        max: null,
        color: MAP_LEGEND_COLOR_HIGH,
        show: true,
    });

    return newLegends;
};

export const parseMapNumericValue = (raw) => {
    if (raw == null || raw === '') {
        return null;
    }
    const parsed = typeof raw === 'number' ? raw : parseFloat(raw);
    return Number.isFinite(parsed) ? parsed : null;
};

export const buildCarriedForwardValues = (items, getValue) => {
    if (!Array.isArray(items) || items.length === 0) {
        return [];
    }

    let lastValue = null;
    return items.map((item) => {
        const parsed = parseMapNumericValue(getValue(item));
        if (parsed != null) {
            lastValue = parsed;
            return parsed;
        }
        return lastValue;
    });
};

export const findLegendForValue = (legends, value) => {
    if (value == null || !Array.isArray(legends)) {
        return null;
    }

    return legends.find((legend) => {
        if (!legend.show) {
            return false;
        }
        const minMatch = legend.min === null || value >= legend.min;
        const maxMatch = legend.max === null || value <= legend.max;
        return minMatch && maxMatch;
    }) || null;
};

export const findFallbackLegend = (legends) => {
    const visibleLegends = (legends || []).filter((legend) => legend.show);
    if (!visibleLegends.length) {
        return null;
    }

    for (let i = visibleLegends.length - 1; i >= 0; i--) {
        if (visibleLegends[i].max == null) {
            return visibleLegends[i];
        }
    }

    return visibleLegends[0];
};

export const getHistoryPointCoords = (item) => {
    const lat = item.c_lat != null ? item.c_lat : item.lat;
    const lng = item.c_lng != null ? item.c_lng : item.lng;
    const alt = item.c_alt != null ? item.c_alt : item.alt;
    return {
        lat: parseFloat(lat),
        lng: parseFloat(lng),
        alt: alt != null ? parseFloat(alt) : undefined,
    };
};

export const hasValidHistoryCoords = (item) => {
    const lat = item.c_lat != null ? item.c_lat : item.lat;
    const lng = item.c_lng != null ? item.c_lng : item.lng;
    return lat != null && lng != null && !isNaN(parseFloat(lat)) && !isNaN(parseFloat(lng));
};

export const getHistCoordsPoint = (item) => ({
    lat: parseFloat(item.lat),
    lng: parseFloat(item.lng),
    alt: item.alt != null ? parseFloat(item.alt) : undefined,
});

export const PLANTIO_STATUS_INICIADO = 'INICIADO';
export const PULV_STATUS_INICIADO = 'INICIADO';
export const DEFAULT_TRACE_OPACITY = 0.5;
export const INACTIVE_TRACE_COLOR = '#9e9e9e';
export const INACTIVE_TRACE_STROKE_WEIGHT = 2;

export const createInactiveTraceStyle = () => ({
    color: INACTIVE_TRACE_COLOR,
    opacity: DEFAULT_TRACE_OPACITY,
    thinLine: true,
    strokeWeight: INACTIVE_TRACE_STROKE_WEIGHT,
});

export const normalizeOperacaoStatus = (status) => {
    if (status == null || status === '') {
        return null;
    }
    return String(status).trim().toUpperCase().replace(/\s+/g, '_');
};

/**
 * Traçado colorido (ativo) somente com status INICIADO.
 * Qualquer outro status (PARADO, EM_PAUSA, DESCONECTADO, FINALIZADO, etc.) ou ausência → linha cinza fina.
 * Usado tanto para plantio (plantio_ponto_status) quanto pulverização (pulv_ponto_status).
 */
export const isOperacaoStatusIniciado = (status) =>
    normalizeOperacaoStatus(status) === PLANTIO_STATUS_INICIADO;

export const isPlantioStatusIniciado = (status) => isOperacaoStatusIniciado(status);

export const isPulvStatusIniciado = (status) => isOperacaoStatusIniciado(status);

export const toCoordIdKey = (value) => {
    if (value == null || value === '') {
        return null;
    }
    const asNumber = Number(value);
    return Number.isFinite(asNumber) ? asNumber : value;
};

/** Largura do implemento em metros.
 * Preferência: plantio → pulverização → GPS (eq_largura_aplicacao / eq_largura, já em metros).
 */
export const resolveImplementWidthMeters = (trabalho) => {
    const fromPlantio = resolveWidthFromLineConfig(
        trabalho?.plantio?.config_qtd_linhas,
        trabalho?.plantio?.config_espacamento_cm
    );
    if (fromPlantio != null) {
        return fromPlantio;
    }

    const fromPulv = resolveWidthFromLineConfig(
        trabalho?.pulv?.config_qtd_linhas,
        trabalho?.pulv?.config_espacamento_cm
    );
    if (fromPulv != null) {
        return fromPulv;
    }

    return resolveWidthFromGps(trabalho?.gps);
};

/**
 * Config de mapa de relatório por trabalho (largura, espaçamento, qtd linhas).
 * Usado no overlay multi-trabalho para cada traçado seguir o próprio implemento.
 */
export const resolveReportMapConfigFromTrabalho = (trabalho) => {
    if (!trabalho) {
        return {};
    }

    const plantioLineCount = Number(trabalho?.plantio?.config_qtd_linhas);
    const pulvLineCount = Number(trabalho?.pulv?.config_qtd_linhas);
    const plantioSpacingCm = Number(trabalho?.plantio?.config_espacamento_cm);
    const pulvSpacingCm = Number(trabalho?.pulv?.config_espacamento_cm);

    const configuredLineCount =
        Number.isFinite(plantioLineCount) && plantioLineCount > 0
            ? plantioLineCount
            : Number.isFinite(pulvLineCount) && pulvLineCount > 0
                ? pulvLineCount
                : undefined;

    const spacingCm =
        Number.isFinite(plantioSpacingCm) && plantioSpacingCm > 0
            ? plantioSpacingCm
            : Number.isFinite(pulvSpacingCm) && pulvSpacingCm > 0
                ? pulvSpacingCm
                : undefined;

    return {
        implementWidthMeters: resolveImplementWidthMeters(trabalho),
        lineWidthMeters: spacingCm != null ? spacingCm / 100 : undefined,
        configuredLineCount,
        usaSementesMetroSegmento: trabalho?.plantio?.usa_sementes_metro_segmento,
    };
};

/** Agrupa o histórico de linhas (histlinhas) pelo campo `numero`. */
export const groupHistoryByLine = (history) => {
    const groups = {};
    (history || []).forEach((item) => {
        const lineNumber = item.numero;
        if (!groups[lineNumber]) {
            groups[lineNumber] = [];
        }
        groups[lineNumber].push(item);
    });
    return Object.entries(groups).map(([numero, items]) => ({
        lineNumber: Number(numero),
        history: items,
    }));
};

export const countGroupedHistoryLines = (histLinhas) => groupHistoryByLine(histLinhas).length;

/**
 * Linhas com população 0 não participam da contagem efetiva
 * (ex.: linhas desativadas em plantio alternado ainda vindas no ponto).
 * Sem o campo `populacao`, a linha continua contando (compatível com histórico parcial).
 */
export const filterLinesParticipatingInCount = (lines) =>
    (lines || []).filter((item) => {
        if (item == null) return false;
        if (item.populacao == null) return true;
        return Number(item.populacao) !== 0;
    });

/**
 * Detecta plantio alternado: histórico tem ~metade das linhas configuradas
 * (ex.: config 16, histórico 8) — no mapa isso dobra a largura do traço.
 */
export const isAlternatingLineConfiguration = (groupedLineCount, registeredLineCount) => {
    if (!groupedLineCount || !registeredLineCount) {
        return false;
    }

    const half = registeredLineCount / 2;
    return groupedLineCount === half || groupedLineCount === half + 0.5;
};

/**
 * Quantidade de linhas para exibição (input / fallback do mapa).
 * Ignora linhas com população 0; se indicar configuração alternada, usa a qtd efetiva;
 * senão prioriza a config do plantio/pulv; por fim o máximo de `numero` nas linhas ativas.
 */
export const resolveDisplayLineCount = (configuredLineCount, histLinhas) => {
    const activeLinhas = filterLinesParticipatingInCount(histLinhas);
    const groupedLineCount = countGroupedHistoryLines(activeLinhas);
    const configured = Number(configuredLineCount);
    const hasConfigured = Number.isFinite(configured) && configured > 0;

    if (groupedLineCount > 0 && hasConfigured && isAlternatingLineConfiguration(groupedLineCount, configured)) {
        return groupedLineCount;
    }

    if (hasConfigured) {
        return configured;
    }

    if (groupedLineCount > 0) {
        return groupedLineCount;
    }

    const numeros = activeLinhas
        .map((item) => item.numero)
        .filter((numero) => numero != null);
    return numeros.length ? Math.max(...numeros) : null;
};

/**
 * Espaçamento entre linhas (cm) para exibição no input.
 * Se o histórico indicar plantio alternado (metade das linhas da config),
 * dobra o espaçamento configurado — espelhando a largura efetiva do mapa.
 */
export const resolveDisplayLineSpacingCm = (spacingCm, configuredLineCount, histLinhas) => {
    if (spacingCm == null || spacingCm === '') {
        return null;
    }

    const spacing = Number(spacingCm);
    if (!Number.isFinite(spacing)) {
        return spacingCm;
    }

    const activeLinhas = filterLinesParticipatingInCount(histLinhas);
    const groupedLineCount = countGroupedHistoryLines(activeLinhas);
    const configured = Number(configuredLineCount);
    const hasConfigured = Number.isFinite(configured) && configured > 0;

    if (groupedLineCount > 0 && hasConfigured && isAlternatingLineConfiguration(groupedLineCount, configured)) {
        return spacing * 2;
    }

    return spacingCm;
};

/** Aplica strokeWidthMeters em segmentos ativos (não thinLine). */
export const applyStrokeWidthToPaths = (paths, strokeWidthMeters) => {
    if (!Array.isArray(paths) || !(strokeWidthMeters > 0)) {
        return paths || [];
    }
    for (const path of paths) {
        if (path && !path.thinLine) {
            path.strokeWidthMeters = strokeWidthMeters;
        }
    }
    return paths;
};

/**
 * Marca segmentos ativos com metadados de borda por trabalho (cor/id/label).
 * A borda visual é desenhada no MapPanel como contorno unificado (union),
 * não stroke por segmento — evita poluição no sementes/metro e mapas densos.
 */
export const applyWorkBorderToPaths = (paths, workMeta) => {
    if (!Array.isArray(paths) || !workMeta?.color) {
        return paths || [];
    }
    for (const path of paths) {
        if (path && !path.thinLine) {
            path.borderColor = workMeta.color;
            path.trabalhoId = workMeta.id ?? workMeta.key;
            path.workLabel = workMeta.label;
        }
    }
    return paths;
};

/** Extrai legenda de bordas a partir dos paths (uma entrada por trabalho). */
export const extractWorkBorderLegendFromPaths = (paths) => {
    if (!Array.isArray(paths) || paths.length === 0) {
        return [];
    }
    const seen = new Map();
    for (const path of paths) {
        if (!path?.borderColor) {
            continue;
        }
        const key = path.trabalhoId ?? path.workLabel ?? path.borderColor;
        if (seen.has(key)) {
            continue;
        }
        seen.set(key, {
            id: path.trabalhoId ?? key,
            label: path.workLabel || (path.trabalhoId != null ? `#${path.trabalhoId}` : 'Trabalho'),
            color: path.borderColor,
        });
    }
    return Array.from(seen.values());
};

const resolveWidthFromLineConfig = (lineCountRaw, spacingCmRaw) => {
    const lineCount = Number(lineCountRaw);
    const spacingCm = Number(spacingCmRaw);
    if (Number.isFinite(lineCount) && lineCount > 0 && Number.isFinite(spacingCm) && spacingCm > 0) {
        return lineCount * (spacingCm / 100);
    }
    return undefined;
};

/** GPS Navigator: largura já em metros (preferência: barra de aplicação). */
const resolveWidthFromGps = (gps) => {
    if (!gps || typeof gps !== 'object') {
        return undefined;
    }
    const applicationWidth = Number(gps.eq_largura_aplicacao);
    if (Number.isFinite(applicationWidth) && applicationWidth > 0) {
        return applicationWidth;
    }
    const machineWidth = Number(gps.eq_largura);
    if (Number.isFinite(machineWidth) && machineWidth > 0) {
        return machineWidth;
    }
    return undefined;
};

export const buildStatusByCId = (history) => {
    if (!Array.isArray(history) || history.length === 0) {
        return null;
    }

    return history.reduce((map, item) => {
        const key = toCoordIdKey(item?.c_id);
        if (key != null) {
            map[key] = item.stat;
        }
        return map;
    }, {});
};

/**
 * Gate para mapas baseados em histCoords:
 * - prioriza status de plantio; se não houver, usa pulverização;
 * - propaga o último status conhecido por c_id (igual sementes/metro);
 * - ativo só com INICIADO; qualquer outro status → linha cinza fina;
 * - sem plantio nem pulv, retorna null (sem filtro).
 */
export const buildOperacaoStatusGateFromData = (data) => {
    const histPlantio = data?.[2]?.histPlantio;
    const histPulv = data?.[1]?.histPulv;

    let statusByCId = null;
    let isStatusValid = null;

    if (Array.isArray(histPlantio) && histPlantio.length > 0) {
        statusByCId = buildStatusByCId(histPlantio);
        isStatusValid = isPlantioStatusIniciado;
    } else if (Array.isArray(histPulv) && histPulv.length > 0) {
        statusByCId = buildStatusByCId(histPulv);
        isStatusValid = isPulvStatusIniciado;
    }

    if (!statusByCId || !isStatusValid) {
        return null;
    }

    let lastKnownStatus = null;
    return (item) => {
        const coordId = toCoordIdKey(item?.id != null ? item.id : item?.c_id);
        if (coordId != null && Object.prototype.hasOwnProperty.call(statusByCId, coordId)) {
            lastKnownStatus = statusByCId[coordId];
        }
        return isStatusValid(lastKnownStatus);
    };
};

const pushInactivePathPoint = (paths, curpoint, lastItem, distance, maxSegmentGapMeters, forceNewPath) => {
    const lastSegment = paths[paths.length - 1];
    const needCreatePath =
        forceNewPath ||
        paths.length === 0 ||
        !lastSegment?.thinLine ||
        (lastItem && distance > maxSegmentGapMeters);

    if (needCreatePath) {
        if (lastSegment?.thinLine && lastItem && distance <= maxSegmentGapMeters && !forceNewPath) {
            lastSegment.path.push(curpoint);
        }
        paths.push({
            path: [curpoint],
            ...createInactiveTraceStyle(),
        });
    } else if (lastSegment?.thinLine) {
        lastSegment.path.push(curpoint);
    }
};

/**
 * Monta segmentos coloridos espelhando o frontend Vue:
 * - propaga o último valor válido;
 * - mantém curLegend entre pontos sem leitura;
 * - faz ponte na mudança de cor (distância <= gap);
 * - usa fallback de legenda nos primeiros pontos sem leitura.
 * - shouldIncludeItem / isActiveItem: pontos inativos viram linha cinza fina
 *   (plantio/pulverização parada), em vez de serem ocultados.
 */
export const buildColoredMapPaths = ({
    items,
    legends,
    getValue,
    getCoords,
    maxSegmentGapMeters = 20,
    shouldIncludeItem = null,
    isActiveItem = null,
}) => {
    if (!Array.isArray(items) || items.length === 0 || !Array.isArray(legends) || legends.length === 0) {
        return [];
    }

    const visibleLegends = legends.filter((legend) => legend.show);
    if (!visibleLegends.length) {
        return [];
    }

    const resolveIsActive = isActiveItem || shouldIncludeItem;
    const carriedValues = buildCarriedForwardValues(items, getValue);
    const paths = [];
    let curLegend = null;
    let lastItem = null;
    let forceNewPath = false;
    let lastWasActive = null;

    for (let i = 0; i < items.length; i++) {
        const item = items[i];
        const isActive = typeof resolveIsActive === 'function' ? resolveIsActive(item) : true;

        if (lastWasActive != null && lastWasActive !== isActive) {
            forceNewPath = true;
        }

        const coords = getCoords(item);
        const curpoint = { lat: coords.lat, lng: coords.lng };

        const lastCoords = lastItem ? getCoords(lastItem) : coords;
        const distance = lastItem
            ? values.distanceBetweenpointsM(
                lastCoords.lat,
                lastCoords.lng,
                lastCoords.alt,
                coords.lat,
                coords.lng,
                coords.alt
            )
            : 0;

        if (!isActive) {
            pushInactivePathPoint(paths, curpoint, lastItem, distance, maxSegmentGapMeters, forceNewPath);
            forceNewPath = false;
            lastItem = item;
            lastWasActive = false;
            curLegend = null;
            continue;
        }

        let needCreatePath = paths.length === 0 || forceNewPath || paths[paths.length - 1]?.thinLine;
        if (lastItem && distance > maxSegmentGapMeters) {
            needCreatePath = true;
        }

        const effectiveValue = carriedValues[i];
        const matchedLegend = effectiveValue != null
            ? findLegendForValue(visibleLegends, effectiveValue)
            : null;

        if (matchedLegend) {
            if (curLegend && curLegend.id !== matchedLegend.id) {
                needCreatePath = true;
            }
            curLegend = matchedLegend;
        }

        if (needCreatePath) {
            if (paths.length > 0 && lastItem && distance <= maxSegmentGapMeters && !forceNewPath && !paths[paths.length - 1]?.thinLine) {
                paths[paths.length - 1].path.push(curpoint);
            }

            if (!curLegend) {
                curLegend = findFallbackLegend(legends);
            }

            if (curLegend && curLegend.show) {
                paths.push({
                    path: [curpoint],
                    color: curLegend.color,
                    opacity: DEFAULT_TRACE_OPACITY,
                });
            }
        } else if (paths.length > 0) {
            if (curLegend && curLegend.show) {
                paths[paths.length - 1].path.push(curpoint);
            } else if (!curLegend) {
                curLegend = findFallbackLegend(legends);
                if (curLegend) {
                    paths[paths.length - 1].color = curLegend.color;
                    paths[paths.length - 1].path.push(curpoint);
                }
            }
        }

        forceNewPath = false;
        lastItem = item;
        lastWasActive = true;
    }

    return paths;
};

/**
 * Datasets por trabalho. Preferência para data.works (overlay multi-trabalho);
 * caso contrário trata data como um único trabalho (formato legado).
 */
export const getReportWorksData = (data) => {
    if (Array.isArray(data?.works) && data.works.length > 0) {
        return data.works;
    }
    return data ? [data] : [];
};

/**
 * Monta paths coloridos por trabalho e concatena os segmentos no mapa.
 * Evita colisão de c_id/id e vazamento de status entre trabalhos no overlay.
 * Com 2+ trabalhos, marca borderColor/trabalhoId para outline unificado + legenda.
 */
export const buildColoredMapPathsPerWork = ({
    data,
    getItems,
    getShouldIncludeItem = null,
    legends,
    getValue,
    getCoords,
    maxSegmentGapMeters = 20,
    shouldIncludeItem = null,
    isActiveItem = null,
    fallbackImplementWidthMeters = null,
}) => {
    if (typeof getItems !== 'function') {
        return [];
    }

    const works = getReportWorksData(data);
    const showBorders = works.length > 1;
    const paths = [];

    works.forEach((workData, index) => {
        const items = getItems(workData);
        if (!Array.isArray(items) || items.length === 0) {
            return;
        }

        const workShouldInclude =
            typeof getShouldIncludeItem === 'function'
                ? getShouldIncludeItem(workData)
                : shouldIncludeItem;

        const workPaths = buildColoredMapPaths({
            items,
            legends,
            getValue,
            getCoords,
            maxSegmentGapMeters,
            shouldIncludeItem: workShouldInclude,
            isActiveItem,
        });

        const workWidth =
            workData?.mapConfig?.implementWidthMeters ?? fallbackImplementWidthMeters;
        applyStrokeWidthToPaths(workPaths, workWidth);

        if (showBorders) {
            applyWorkBorderToPaths(workPaths, getWorkSeriesMeta(workData, index));
        }

        paths.push(...workPaths);
    });

    return paths;
};
